import { Button } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { FiSearch } from 'react-icons/fi'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { FilterPopover } from '@/shared/components/filter-popover'
import DebouncedInput from '@/shared/components/debounced-input'
import { AccreditationSelector, StatusSelector } from '@/shared/selectors'
import { CompanySizeSelector } from '@/shared/selectors/company-size-selector/company-size-selector'
import { list } from '@/router/utils'
import { useGetServiceAreasQuery } from '@/store/services/service-area.service'
import { toOptions } from '@/utils'
import CustomSelect from '@/shared/components/custom-select'
import { useMemo } from 'react'

export const CompanyFilter = ({ open, handler }) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { data: serviceAreas = [], isLoading: isLoadingServiceAreas } = useGetServiceAreasQuery('per_page=999')

  const serviceAreaOptions = useMemo(() => {
    return toOptions({ data: serviceAreas })
  }, [serviceAreas])

  const selectedServiceArea = serviceAreaOptions.find((option) => {
    return String(option.value) === searchParams.get('service_area_id')
  })

  const updateSearchParams = (updater) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set('page', '1')
    updater(nextSearchParams)
    setSearchParams(nextSearchParams)
  }

  const searchQueryHandler = (value) => {
    updateSearchParams((nextSearchParams) => {
      if (value === '' || value === null || value === undefined) {
        nextSearchParams.delete('q')
      } else {
        nextSearchParams.set('q', value)
      }
    })
  }

  const handleCompanySizeQuery = (selectedOption) => {
    updateSearchParams((nextSearchParams) => {
      if (selectedOption === null || selectedOption === undefined || selectedOption.value === '') {
        nextSearchParams.delete('size')
      } else {
        nextSearchParams.set('size', selectedOption.value)
      }
    })
  }

  const handleStatusQuery = ({ value: status }) => {
    updateSearchParams((nextSearchParams) => {
      if (status === null || status === '') {
        nextSearchParams.delete('status')
      } else {
        nextSearchParams.set('status', status)
      }
    })
  }

  const handleAccreditedQuery = ({ value: accredited }) => {
    updateSearchParams((nextSearchParams) => {
      if (accredited === null || accredited === '') {
        nextSearchParams.delete('accredited')
      } else {
        nextSearchParams.set('accredited', accredited)
      }
    })
  }

  const handleServiceAreaQuery = (selectedOption) => {
    updateSearchParams((nextSearchParams) => {
      nextSearchParams.delete('service_area_id')

      if (!selectedOption?.value) return

      nextSearchParams.set('service_area_id', selectedOption.value)
    })
  }

  const removeQueries = () => {
    navigate(list('companies'))
    setSearchParams({})
  }

  return (
    <div className='flex gap gap-3'>
      <div>
        <DebouncedInput
          icon={<FiSearch />}
          label={t('company.searcher.label')}
          onChange={searchQueryHandler}
          value={searchParams.get('q')}
          className='w-full sm:w-96'
        />
      </div>

      <FilterPopover open={open} handler={handler}>
        <div className='grid gap gap-4'>
          <div className='grid gap gap-2'>
            <CompanySizeSelector
              onChange={handleCompanySizeQuery}
              value={searchParams.get('size') || ''}
            />

            <StatusSelector
              value={searchParams.get('status') || null}
              onChange={handleStatusQuery}
            />
            <AccreditationSelector
              value={searchParams.get('accredited') || null}
              onChange={handleAccreditedQuery}
            />
            <CustomSelect
              isClearable
              isLoading={isLoadingServiceAreas}
              placeholder={t('serviceArea.one')}
              options={serviceAreaOptions}
              value={selectedServiceArea || null}
              onChange={handleServiceAreaQuery}
            />
          </div>
        </div>

        <div className='flex justify-center mt-10'>
          <Button
            type='button'
            className='bg-color-white text-red-600 border-solid border-2 border-red-200 p-2'
            onClick={removeQueries}
          >
            {t('filters.delete')}
          </Button>
        </div>
      </FilterPopover>
    </div>
  )
}

CompanyFilter.propTypes = {
  open: PropTypes.bool,
  handler: PropTypes.func
}
